import { immerable } from 'immer'
import { workerCode } from '../worker'
import { filterStore } from '../FilterComponent'

/** @class BaseFilter フィルター生成ロジックを継承するためのクラス */
export class BaseFilter {
  // ジョッブの処理
  processPerLevel = async (result) => {
    const { FilterableDepartmentList = [], OriginalDepartmentNameList = [] } = result

    const MAX_WORKER_THREADS = navigator.hardwareConcurrency || 4
    const rowLength = FilterableDepartmentList.length
    const workerChunkSize = Math.ceil(rowLength / MAX_WORKER_THREADS)

    const promises = []

    for (let i = 0; i < MAX_WORKER_THREADS; i++) {
      const start = i * workerChunkSize
      const end = Math.min(start + workerChunkSize, rowLength)
      const slice = FilterableDepartmentList.slice(start, end)
      const slice2 = OriginalDepartmentNameList.slice(start, end)

      promises.push(this.createJob(slice, slice2, start))
    }

    const results = await Promise.all(promises)

    return this.mergeResults(results)
  }

  //ワーカーの結果をマージ
  mergeResults = (results) => {
    return results
      .sort((a, b) => a.startIndex - b.startIndex)
      .reduce((acc, current) => {
        const { result } = current

        for (let i = 0; i < result.length; i++) {
          const descendantPerIndex = result[i] // object of descendants

          Object.keys(descendantPerIndex).forEach((key) => {
            if (acc[i]?.[key] === undefined) {
              acc[i] = { ...acc[i], [key]: descendantPerIndex[key] }
            } else {
              const { descendants, ...rest } = descendantPerIndex[key]

              const seen = new Set(acc[i][key].descendants?.map((e) => e.toString()))

              descendants.forEach((e) => {
                if (!seen.has(e.toString())) acc[i][key].descendants.push(e)
              })

              acc[i] = { ...acc[i], [key]: { ...acc[i][key], ...rest } }
            }
          })
        }

        return acc
      }, [])
  }

  //ジョッブの作成
  createJob = async (FilterableDepartmentList, OriginalDepartmentNameList, startIndex) => {
    const blobUrl = URL.createObjectURL(new Blob([workerCode]))

    const worker = new Worker(blobUrl)

    return await new Promise((resolve, reject) => {
      worker.onmessage = (e) => {
        resolve({ result: e.data, startIndex })

        URL.revokeObjectURL(blobUrl)
        worker.terminate()
      }
      worker.onerror = (e) => {
        console.error('Error from worker:', e.message)
        console.error('Filename:', e.filename)
        console.error('Line number:', e.lineno, 'Column number:', e.colno)
        reject(e)
        URL.revokeObjectURL(blobUrl)
        worker.terminate()
      }

      worker.postMessage([FilterableDepartmentList, OriginalDepartmentNameList, startIndex])
    }).catch(console.error)
  }

  /**
   * @function generateOutput
   * @param {{ optionState: Record<string, unknown>; controlState: Record<string, unknown> }} filterState フィルターの状態
   */
  generateOutput(filterState) {
    const { optionState: options, controlState: controls } = filterState

    const computed = Object.fromEntries(Array.from(controls.keys()).map((e) => [e, []]))

    controls.entries().forEach(([key, control]) => {
      switch (control.type) {
        default:
          break
        case 'radio':
        case 'checkbox':
        case 'select': {
          const id = control.optionIdList.find((e) => options.get(e).state.value)
          if (!id) break

          computed[key] = [options.get(id).value].filter(Boolean)
          break
        }
        case 'multiselect': {
          const values = controls
            .get(key)
            .optionIdList.map((e) => {
              const option = options.get(e)

              if (!option?.state?.value) return ''
              return option?.value
            })
            .filter(Boolean)

          computed[key] = values.length === 0 ? ['-'] : values
          break
        }
        case 'text': {
          const optionId = control.optionIdList?.[0]
          computed[key] = [options.get(optionId).state.value]
          break
        }
      }
    })

    if (window.debug_) console.log(computed)

    return computed
  }

  init() {
    throw Error('Un Implemented')
  }
  /**
   * @function getConfig
   * @returns {{ options: Record<string, Option>; controls: Record<string, Control>; groups: Group[] }}
   */
  getConfig() {
    throw Error('Un Implemented')
  }
}

/** @class BaseControl コントロールのための継承用クラス */
export class BaseControl {
  [immerable] = true
  /** @type {Control['id']} */
  id
  /** @type {Control['label']} */
  label = ''
  /** @type {Control['type']} */
  type = 'multiselect'
  /** @type {Control['optionIdList']} */
  optionIdList = []
  /** @type {Control['values']} */
  values = []
  /** @type {Control['className']} */
  className = ''
  /** @type {Control['labelClassName']} */
  labelClassName = 'mb-2'
  /** @type {Control['state']} */
  state = { show: true, disabled: false }
  /** @type {Control['meta']} */
  meta = {}

  /** @param {Record<string, Option>} options */
  constructor(id, label, type) {
    this.id = id
    this.label = label
    this.type = type
  }

  /**
   * @function 選択を設定する
   * @param {string[]} optionIdList
   */
  setOptionIdList(optionIdList) {
    this.optionIdList = optionIdList
    return this
  }

  /**
   * @function 選択の値を設定する
   * @param {string[]} optionIdList
   */
  setValues(values = []) {
    this.values = values
    return this
  }

  /**
   * @function 選択をフィルターする
   * @param {string[]} optionIdList
   */
  filterOptionList(optionIdList = []) {
    return this.optionIdList.filter((e) => !optionIdList.includes(e))
  }

  /**
   * リセット処理
   *
   * @param {Record<string, Control>} controlState //フィルタの状態
   * @param {function( any ):void} setControlState //
   * @param {function( any ):void} setOptionState
   */
  reset(controlState, setControlState, setOptionState) {
    if (this.type === 'multiselect') {
      controlState[this.id].values = this.optionIdList
    } else {
      controlState[this.id].values = [this.optionIdList?.[0]]
    }

    setControlState(controlState)
  }
}

export class BaseOption {
  [immerable] = true
  id
  label = '' // 選択のラベル
  displayLabel = undefined // 選択された時の表示
  value = '' // QuickSuiteに送る値
  state = { show: true, disabled: false, value: null } // valueはフロント側で使う値
  meta = {} // メタ情報
  extra = ''
  labelClassName = ''
  valueClassName = ''
  onChange = (value) => {
    return null
  }

  /**
   * @param {string} id // ID
   * @param {string} label //ラベル
   * @param {string} value // 値
   */
  constructor(id, label, value) {
    this.id = id
    this.label = label
    this.value = value
  }

  setState(field, value) {
    this.state[field] = value
  }

  /**
   * 値を設定する
   *
   * @param {string} field
   * @param {string} value
   * @returns {BaseOption}
   */
  set(field, value) {
    Object.defineProperty(this, field, {
      value,
      writable: true,
      enumerable: true,
      configurable: true
    })
    return this
  }
}

export class BasicFilterStore {
  store = null

  getStoreData() {
    return this.store.getState()
  }

  constructor() {
    this.store = filterStore
  }

  /**
   * 全選択の状態を更新
   *
   * @param {Map<string, Control>}
   */
  overrideControlState(controlState) {
    this.store.dispatch({ type: 'SET_CONTROL', payload: controlState })
  }

  /**
   * フィルター項目の状態更新
   *
   * @param {string} id
   * @param {string} path
   * @param {any} value
   */
  updateControlState(id, path, value) {
    this.store.dispatch({ type: 'SET_CONTROL_STATE', payload: { id, value, path } })
  }

  /**
   * @param {string} id
   * @param {string} value
   */
  updateControlValue(id, value) {
    this.store.dispatch({ type: 'SET_CONTROL_VALUE', payload: { id, value } })
  }

  /**
   * 全選択肢の状態を更新
   *
   * @param {Map<string, Option>}
   */
  overrideOptionState(optionState) {
    this.store.dispatch({ type: 'SET_OPTION', payload: optionState })
  }

  /**
   * 選択肢の状態更新
   *
   * @param {string} id
   * @param {string} path
   * @param {any} value
   */
  updateOptionState(id, path, value) {
    this.store.dispatch({ type: 'SET_OPTION_STATE', payload: { id, value, path } })
  }

  /**
   * 選択肢のバリューを更新
   *
   * @param {string} id
   * @param {string} value
   */
  updateOptionValue(id, value) {
    this.store.dispatch({ type: 'SET_OPTION_VALUE', payload: { id, value } })
  }
}
